import { useCallback, useEffect, useMemo } from 'react';
import { RouteProps, useLocation } from 'react-router';
import { useObservableCacheItem } from '../../cache/observable-cache-hooks';
import { useObservableCache } from '../../cache/observable-cache-provider';
import { useBeaconRouter } from '../../route/route.hooks';
import { Route } from '../../route/route.types';

interface NavHistoryHelpers {
  updateLocationHistory: (input: SetLocationHistoryInput) => Promise<void>;
}

interface NavUserToLastKnownLocationResults {
  navToLastKnownTenantLocation: (tenantId: string) => void;
}

export interface SetLocationHistoryInput {
  location: RouteProps['location'];
  tenantId: string;
}

export interface NavHistoryState {
  [tenantId: string]: {
    location: RouteProps['location'] | null;
  };
}

const NAV_CACHE_KEY = 'navHistory';

const isLocationInBlacklist = (input: SetLocationHistoryInput) => {
  // Blacklist is inside because Routes is undefined outside this function
  const locationHistoryBlacklist = ['/auth'];

  const idx = locationHistoryBlacklist.findIndex((route) => {
    const pathname = input.location?.pathname || undefined;
    return pathname && pathname.includes(route);
  });

  return idx > -1;
};

const useNavHistoryHelpers = (): NavHistoryHelpers => {
  const cache = useObservableCache();

  return useMemo(
    () => ({
      updateLocationHistory: async (input: SetLocationHistoryInput) => {
        const inBlacklist = isLocationInBlacklist(input);

        if (inBlacklist) {
          return;
        }

        const current = await cache.getItem<NavHistoryState>(NAV_CACHE_KEY);

        await cache.setItem(NAV_CACHE_KEY, {
          ...current,
          [input.tenantId]: {
            location: input.location,
          },
        });
      },
    }),
    [cache]
  );
};

const useNavLocationHistoryLazy = (): ((
  tenantId: string
) => RouteProps['location'] | null) => {
  const allTenantHistory =
    useObservableCacheItem<NavHistoryState>(NAV_CACHE_KEY);

  return useCallback(
    (tenantId) => {
      if (!allTenantHistory) {
        return null;
      }

      return allTenantHistory[tenantId]?.location ?? null;
    },
    [allTenantHistory]
  );
};

const useNavToLastKnownLocation = (): NavUserToLastKnownLocationResults => {
  const getLastLocationForTenant = useNavLocationHistoryLazy();
  const router = useBeaconRouter();

  const navToLastKnownTenantLocation = useCallback(
    async (tenantId: string) => {
      const lastLocation = getLastLocationForTenant(tenantId);

      const { builder, performWindowNavigation } = router;

      let url = builder.getHomeRoute({
        prefix: tenantId,
      });

      if (lastLocation?.pathname) {
        let prefix = `/${tenantId}`;

        if (
          lastLocation.pathname.startsWith(`/${tenantId}`) ||
          lastLocation.pathname.startsWith(`${Route.DIRECT_CONNECT}`)
        ) {
          prefix = '';
        }

        url = builder.build(lastLocation.pathname, {
          prefix,
          queryParams: lastLocation.search,
        });
      }

      performWindowNavigation(url);
    },
    [getLastLocationForTenant, router]
  );

  return { navToLastKnownTenantLocation };
};

const usePersistNavigationHistory = (tenantId: string): void => {
  const { updateLocationHistory } = useNavHistoryHelpers();
  const location = useLocation();

  useEffect(() => {
    if (location) {
      updateLocationHistory({ tenantId, location });
    }
  }, [location, tenantId, updateLocationHistory]);
};

const useClearNavigationHistory = (): ((tenantId: string) => void) => {
  const { updateLocationHistory } = useNavHistoryHelpers();

  const callback = useCallback(
    (tenantId: string) => {
      updateLocationHistory({
        tenantId,
        location: undefined,
      });
    },
    [updateLocationHistory]
  );

  return callback;
};

export {
  useNavLocationHistoryLazy,
  useNavToLastKnownLocation,
  usePersistNavigationHistory,
  useClearNavigationHistory,
};
